import { useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Context Providers
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ProductProvider, useProducts } from "./context/ProductContext";

// Layout Components (always in initial bundle — visible on every page)
import { Navbar } from "./components/layout/Navbar";

// Above-the-fold pages — eager import for fast LCP
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";

// Section components used across multiple routes — eager (small, shared)
import {
  AnimeSection,
  StreetwearSection,
  HoodiesSection,
  HipHopSection,
  FaithSection,
  SportsSection,
  BlackHistorySection,
  AdoreSection,
  LadiesSection,
  ChildrenSection,
} from "./components/sections";

// Route-level code splitting — deferred until user navigates to them
const CartPage = lazy(() => import("./pages/CartPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const OurStoryPage = lazy(() => import("./pages/OurStoryPage"));
const LifestylePage = lazy(() => import("./pages/LifestylePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const NewDropsPage = lazy(() => import("./pages/NewDropsPage"));
const ShayAdorePage = lazy(() => import("./pages/ShayAdorePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const OrderCancelPage = lazy(() => import("./pages/OrderCancelPage"));
// Admin bundle — never loaded for customers
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AddProductPage = lazy(() => import("./pages/AddProductPage"));
const AdminSubscribersPage = lazy(() => import("./pages/AdminSubscribersPage"));
const AdminOrdersPage = lazy(() => import("./pages/AdminOrdersPage"));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="route-loading">
      <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppContent() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { products } = useProducts();

  return (
    <div className="App bg-black min-h-screen">
      <BrowserRouter>
        <Navbar onMenuClick={() => setMenuOpen(!menuOpen)} menuOpen={menuOpen} />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage products={products} />} />
            <Route path="/shop" element={<ShopPage products={products} />} />
            <Route path="/our-story" element={<OurStoryPage />} />
            <Route path="/lifestyle" element={<LifestylePage />} />
            <Route path="/hiphop" element={<HipHopSection products={products} isPage={true} />} />
            <Route path="/faith" element={<FaithSection products={products} isPage={true} />} />
            <Route path="/sports" element={<SportsSection products={products} isPage={true} />} />
            <Route path="/streetwear" element={<StreetwearSection products={products} isPage={true} />} />
            <Route path="/streetwear/ladies" element={<LadiesSection products={products} isPage={true} />} />
            <Route path="/streetwear/ladies/:capsule" element={<LadiesSection products={products} isPage={true} />} />
            <Route path="/hoodies" element={<HoodiesSection products={products} isPage={true} />} />
            <Route path="/anime" element={<AnimeSection products={products} isPage={true} />} />
            <Route path="/children" element={<ChildrenSection products={products} isPage={true} />} />
            <Route path="/blackhistory" element={<BlackHistorySection products={products} isPage={true} />} />
            <Route path="/adore" element={<AdoreSection products={products} isPage={true} />} />
            <Route path="/product/:productId" element={<ProductDetailPage products={products} />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/add-product" element={<AddProductPage />} />
            <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/shay-adore" element={<ShayAdorePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/new-drops" element={<NewDropsPage />} />
            <Route path="/order/success" element={<OrderSuccessPage />} />
            <Route path="/order/cancel" element={<OrderCancelPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProductProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </ProductProvider>
    </AuthProvider>
  );
}

export default App;
